import { useState } from "react";
import {
  Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, Area, ComposedChart,
} from "recharts";
import { InfoTip } from "./InfoTip";
import type { LiquidityData, LarScenario } from "../types";

interface SlippageCurveChartProps {
  liquidity: LiquidityData;
  lar?: Record<string, LarScenario>;
}

export function SlippageCurveChart({ liquidity, lar }: SlippageCurveChartProps) {
  const curveLabels = Object.keys(liquidity.curves).sort();
  const [selected, setSelected] = useState(curveLabels[0] ?? "");

  if (curveLabels.length === 0) return null;

  const curve = liquidity.curves[selected];
  if (!curve || curve.points.length === 0) return null;

  // Build chart data, cap slippage at 50% for readability
  const chartData = curve.points
    .filter((p) => p.slippage_pct <= 0.5)
    .map((p) => ({
      size: p.trade_size_usd,
      sizeLabel: `$${(p.trade_size_usd / 1e6).toFixed(0)}M`,
      slippage: p.slippage_pct * 100,
    }));

  // Add a point at the next size showing it's off the chart if we filtered
  if (chartData.length < curve.points.length && chartData.length > 0) {
    const next = curve.points[chartData.length];
    chartData.push({
      size: next.trade_size_usd,
      sizeLabel: `$${(next.trade_size_usd / 1e6).toFixed(0)}M`,
      slippage: 50,
    });
  }

  // Look up actual bonus from LaR data for this pair's collateral
  const collateral = selected.split("\u2192")[0];
  const bonusPct = lookupBonus(collateral, lar);

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold">
            <InfoTip text="Each curve shows how slippage increases with trade size for a liquidation path. The horizontal line marks the liquidation bonus — above it, liquidation is unprofitable. The intersection is the available liquidity.">
              DEX Liquidity Depth
            </InfoTip>
          </h2>
          <p className="text-xs text-text-muted mt-0.5">
            Slippage vs. trade size from Odos aggregator quotes
          </p>
        </div>
      </div>

      {/* Pair selector */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {curveLabels.map((label) => {
          const pts = liquidity.curves[label]?.points ?? [];
          const hasData = pts.length > 0;
          return (
            <button
              key={label}
              onClick={() => hasData && setSelected(label)}
              disabled={!hasData}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                selected === label
                  ? "bg-accent text-white"
                  : hasData
                    ? "bg-surface-700 text-text-secondary hover:text-text-primary hover:bg-surface-600"
                    : "bg-surface-700 text-text-muted opacity-40"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Compute intersection point */}
      {(() => {
        const bonusVal = bonusPct * 100;
        let intersection: {size: number; slippage: number} | null = null;
        for (let i = 0; i < chartData.length - 1; i++) {
          const a = chartData[i];
          const b = chartData[i + 1];
          if (a.slippage <= bonusVal && b.slippage > bonusVal) {
            const frac = (bonusVal - a.slippage) / (b.slippage - a.slippage);
            intersection = {
              size: a.size + frac * (b.size - a.size),
              slippage: bonusVal,
            };
            break;
          }
        }
        if (intersection) {
          chartData.push({
            size: intersection.size,
            sizeLabel: `$${(intersection.size / 1e6).toFixed(1)}M`,
            slippage: intersection.slippage,
          });
          chartData.sort((a, b) => a.size - b.size);
        }
        return null;
      })()}

      {/* No data message */}
      {chartData.length < 2 && (
        <div className="flex items-center justify-center h-40 text-sm text-text-muted">
          Insufficient data points for {selected} (need ≥2, have {curve.points.length})
        </div>
      )}

      {/* Chart */}
      {chartData.length >= 2 && <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: -4 }}>
          <defs>
            <linearGradient id="slippageGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="size"
            tickFormatter={(v: number) => v >= 1e6 ? `$${(v / 1e6).toFixed(0)}M` : `$${(v / 1e3).toFixed(0)}K`}
            tick={{ fontSize: 10, fill: "#64748b" }}
            axisLine={{ stroke: "#282a3a" }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => v < 1 ? `${v.toFixed(1)}%` : `${Math.round(v)}%`}
            tick={{ fontSize: 10, fill: "#64748b" }}
            axisLine={false}
            tickLine={false}
            width={48}
            domain={[0, (max: number) => Math.max(max * 1.2, bonusPct * 100 * 3, 0.5)]}
            ticks={buildYTicks(chartData, bonusPct)}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1e2030",
              border: "1px solid #282a3a",
              borderRadius: "6px",
              fontSize: "11px",
            }}
            labelFormatter={(v: number) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M trade` : `$${(v / 1e3).toFixed(0)}K trade`}
            formatter={(value: number, _name: string, props: any) => {
              const bonusVal = bonusPct * 100;
              const isIntersection = Math.abs(value - bonusVal) < 0.01;
              if (isIntersection) {
                return [`${value.toFixed(2)}% (= bonus threshold)`, `Available liquidity: $${(props.payload.size / 1e6).toFixed(1)}M`];
              }
              return [`${value.toFixed(2)}%`, "Slippage"];
            }}
          />
          {/* Bonus threshold line */}
          <ReferenceLine
            y={bonusPct * 100}
            stroke="#eab308"
            strokeDasharray="6 3"
            strokeWidth={1.5}
          />
          {/* Area fill under curve */}
          <Area
            type="monotone"
            dataKey="slippage"
            fill="url(#slippageGrad)"
            stroke="none"
          />
          {/* Main line */}
          <Line
            type="monotone"
            dataKey="slippage"
            stroke="#ef4444"
            strokeWidth={2}
            dot={(props: any) => {
              const bonusVal = bonusPct * 100;
              const isIntersection = Math.abs(props.payload.slippage - bonusVal) < 0.01;
              if (isIntersection) {
                return (
                  <g key={props.key}>
                    <circle cx={props.cx} cy={props.cy} r={7} fill="#eab308" fillOpacity={0.2} stroke="#eab308" strokeWidth={1.5} />
                    <circle cx={props.cx} cy={props.cy} r={3} fill="#eab308" />
                  </g>
                );
              }
              return <circle key={props.key} cx={props.cx} cy={props.cy} r={3} fill="#ef4444" />;
            }}
            activeDot={(props: any) => {
              const bonusVal = bonusPct * 100;
              const isIntersection = Math.abs(props.payload.slippage - bonusVal) < 0.01;
              return <circle cx={props.cx} cy={props.cy} r={5} fill={isIntersection ? "#eab308" : "#ef4444"} />;
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-text-muted">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 bg-red-500 inline-block" /> Slippage
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-0.5 inline-block" style={{ borderTop: "2px dashed #eab308" }} /> Liquidation bonus ({(bonusPct * 100).toFixed(0)}%)
        </span>
        <span className="ml-auto">
          {curve.points.length} data points · {liquidity.scan_age_seconds != null ? `${(liquidity.scan_age_seconds / 3600).toFixed(1)}h ago` : ""}
        </span>
      </div>
    </div>
  );
}

function buildYTicks(data: Array<{slippage: number}>, bonusPct: number): number[] {
  const bonusVal = bonusPct * 100;
  const maxSlip = Math.max(...data.map((d) => d.slippage), bonusVal * 2, 0.5);
  const ticks: number[] = [0];

  // Always include the bonus value
  ticks.push(Math.round(bonusVal * 10) / 10);

  // Add round-number ticks
  const candidates = [0.5, 1, 2, 5, 10, 20, 30, 40, 50];
  for (const c of candidates) {
    if (c > 0 && c <= maxSlip * 1.2 && Math.abs(c - bonusVal) > 0.3) {
      ticks.push(c);
    }
  }

  return ticks.sort((a, b) => a - b);
}

function lookupBonus(collateral: string, lar?: Record<string, LarScenario>): number {
  if (!lar) return 0.05;
  // Search across all scenarios for this asset's bonus
  for (const scenario of Object.values(lar)) {
    for (const level of scenario.lar_by_drop) {
      const asset = level.assets.find((a) => a.symbol === collateral);
      if (asset && asset.bonus_pct > 0) return asset.bonus_pct;
    }
  }
  return 0.05;
}
